import React, { FunctionComponent, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { CheckCircleOutline, InfoOutlined, ReportProblemOutlined, ErrorOutline, Close } from '../../components/icons';
import cn from '../cn';

type Severity = 'success' | 'info' | 'warning' | 'error';

type SnackbarUIProps = {
  message: string,
  autoHide: boolean,
  context?: React.ReactNode,
  open: boolean,
  onSnackBarClose: () => void,
  horizontalPosition: 'right' | 'left' | 'center',
  severity: Severity,
  showClose?: boolean,
  children?: React.ReactNode,
};

// The toast surface comes from `.ds-toast`; severity only picks the
// `data-ds-tone` that colours its left border. The icon hexes are semantic
// status colours and stay literal.
const alertStyle: Record<Severity, { tone: string; icon: string }> = {
  success: { tone: 'ok', icon: '#2e7d32' },
  info: { tone: 'info', icon: '#0288d1' },
  warning: { tone: 'warn', icon: '#ed6c02' },
  error: { tone: 'danger', icon: '#d32f2f' },
};
const iconFor: Record<Severity, typeof CheckCircleOutline> = {
  success: CheckCircleOutline,
  info: InfoOutlined,
  warning: ReportProblemOutlined,
  error: ErrorOutline,
};

// Bottom-anchored auto-hiding toast (MUI Snackbar + Alert). Same wrapper API.
// Portaled to <body> at MUI's Snackbar z-index (1400); auto-hides after 4s when
// `autoHide` is true (matches the wrapper; persists otherwise).
const SnackbarUI: FunctionComponent<SnackbarUIProps> = ({
  message,
  autoHide,
  context,
  open,
  onSnackBarClose,
  horizontalPosition,
  severity,
  showClose = true,
}) => {
  useEffect(() => {
    if (!open || autoHide !== true) return;
    const t = setTimeout(() => onSnackBarClose(), 4000);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, autoHide]);

  if (!open) return null;

  const s = alertStyle[severity];
  const Icon = iconFor[severity];
  const posClass = horizontalPosition === 'left'
    ? 'left-6'
    : horizontalPosition === 'center' ? 'left-1/2 -translate-x-1/2' : 'right-6';

  return createPortal(
    <div className={cn('fixed bottom-6 z-[1400]', posClass)}>
      <div role="alert" data-ds-tone={s.tone} className="ds-toast min-w-[288px] max-w-[560px] items-center">
        <Icon style={{ color: s.icon, fontSize: 20 }} />
        <span className="flex-1">{message}</span>
        {context}
        {showClose && <Close className="cursor-pointer text-[18px]" onClick={() => onSnackBarClose()} />}
      </div>
    </div>,
    // Portal into #root (not document.body): Tailwind utilities are scoped under
    // `#root` via `important: '#root'`, so a body-level portal would be unstyled.
    document.getElementById('root') ?? document.body,
  );
};

export default SnackbarUI;
